import React, { useContext, useState } from 'react';
import { useMutation } from '@apollo/client';
import {
  Flex,
  Box,
  theme,
  Modal,
  Text,
  Button,
  Link,
  Flow,
} from '@xometry/xometry_loft';
import {
  GetOrganizationByIdQuery,
  GetUsersOrganizationsByIdentityQuery,
  Organization,
  UpdateOrganizationIsArchivedMutationResult,
  UpdateOrganizationIsArchivedMutationVariables,
  UpdateOrganizationIsArchivedQuery,
} from '../../schema/generated/schema';
import { NotificationContext } from '../../contexts/Notification';
interface Props {
  reactivateTeamModalOpen: boolean;
  setReactivateTeamModalOpen: (openState: boolean) => void;
  organization: Organization | undefined;
  refetchInactiveOrganizations: any;
  setSelectedTab?: any;
}
const ReactivateTeamModal = ({
  reactivateTeamModalOpen,
  setReactivateTeamModalOpen,
  organization,
  refetchInactiveOrganizations,
  setSelectedTab,
}: Props) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { addNotification } = useContext(NotificationContext);
  const [updateOrganizationIsArchived] = useMutation<
    UpdateOrganizationIsArchivedMutationResult,
    UpdateOrganizationIsArchivedMutationVariables
  >(UpdateOrganizationIsArchivedQuery, {
    refetchQueries: [
      GetUsersOrganizationsByIdentityQuery,
      'GetUsersOrganizationsByIdentity',
      GetOrganizationByIdQuery,
      'GetOrganizationsById',
    ],
  });
  const reactivateConfirm = async () => {
    setIsSubmitting(true);
    const { data: updateResult } = await updateOrganizationIsArchived({
      variables: {
        orgId: organization?.id,
        isArchived: false,
      },
      onCompleted: () => {
        if (!updateResult?.updateOrganizationIsArchived.isArchived) {
          addNotification(
            'success',
            `${organization?.name} has been successfully reactivated.`
          );
          refetchInactiveOrganizations();
          setReactivateTeamModalOpen(false);
        }
        // Set tab back to my teams if acting from user-settings
        if (setSelectedTab) {
          setSelectedTab(0);
        }
        setIsSubmitting(false);
      },
      onError: () => {
        addNotification(
          'danger',
          `There was an error reactivating ${organization?.name}. Please try again.`
        );
        setReactivateTeamModalOpen(false);
        setIsSubmitting(false);
      },
    });
  };
  const handleClose = () => {
    setReactivateTeamModalOpen(false);
  };
  return (
    <Modal
      id="reactivationModal"
      isOpen={reactivateTeamModalOpen}
      breakpointMaxWidth="634px"
      showClose
      onClose={handleClose}
      title={
        <Text
          padding={theme.space[3]}
          kind="panel-title"
        >{`Are you sure you want to reactivate ${organization?.name}?`}</Text>
      }
    >
      <Box color={theme.colors.darkBlues[100]}>
        <Box margin={theme.space[3]}>
          <Text>
            By reactivating the team {`${organization?.name}`}, you will still
            have access to all quotes and orders associated with this team.
          </Text>
        </Box>

        <Box margin={theme.space[3]}>
          <Text>
            Members of this team will also be notified that the team was
            reactivated.{' '}
            <Link
              fontSize={theme.fontSizes[2]}
              href="https://community.xometry.com/kb/articles/812-how-can-i-delete-a-team-if-it-s-no-longer-needed"
              target="_blank"
            >
              Learn more about archiving teams
            </Link>
            .
          </Text>
        </Box>
      </Box>
      <Flex justifyContent="flex-end" margin={theme.space[3]}>
        <Button
          kind={theme.kind.primaryOutline}
          marginRight={theme.space[2]}
          onClick={handleClose}
        >
          Cancel
        </Button>
        <Button kind={theme.kind.primary} onClick={reactivateConfirm}>
          {isSubmitting ? <Flow kind={'disabled'} /> : 'Reactivate Team'}
        </Button>
      </Flex>
    </Modal>
  );
};

export default ReactivateTeamModal;
